import { formatMask, type MaskBlock } from '@/utils/input-mask-util'
import { useCallback, useMemo } from 'react'

export interface InputMaskProps {
  /**
   * The current value of the string to be masked.
   */
  value: string

  /**
   * The mask to use. If `undefined`, no masking is done.
   */
  mask: MaskBlock[] | undefined

  /**
   * Change handler that is called with the new, formatted value.
   * @param newValue
   */
  onChange: (newValue: string) => unknown
}

/**
 * Applies an input mask to the input value.
 */
export function useInputMask({
  mask,
  value: inputValue,
  onChange,
}: InputMaskProps) {
  const changeHandler = useCallback(
    (newValue: string) => {
      const masked = mask ? formatMask(mask, newValue) : newValue
      onChange?.(masked)
    },
    [mask, onChange],
  )

  const maskedValue = useMemo(
    () => (mask ? formatMask(mask, inputValue) : inputValue),
    [mask, inputValue],
  )

  return {
    maskedValue,
    onChange: mask ? changeHandler : onChange,
  }
}
